<template>
  <!-- The first version of this component assumes images are being uploaded -->
  <!-- (might eventually fork logic according to an expected file type prop) -->
  <div class="file-picker-button">
    <!-- This input is used in desktop environments where cordova is not available -->
    <input type="file" @change="onInputFileChange" accept="image/*" ref="inputFile" hidden />
    <!-- Otherwise we present Camera vs Library buttons to dictate cordova file source -->
    <slot name="default" :on="{ click: onUploadIntent }" :loading="loading">
      <v-btn block :loading="loading" @click="onUploadIntent">PHOTO</v-btn>
    </slot>
    <v-bottom-sheet v-model="presentSourceOptions">
      <v-sheet
        class="py-5 text-center"
      >
        <div class="mx-5">
          <v-btn block @click="onSelectCamera" color="primary" x-large>
            <v-icon left>mdi-camera</v-icon>
            CAMERA
          </v-btn>
        </div>
        <div class="mx-5 mt-5">
          <v-btn block @click="onSelectLibrary" color="primary" x-large>
            <v-icon left>mdi-file</v-icon>
            LIBRARY
          </v-btn>
        </div>
      </v-sheet>
    </v-bottom-sheet>
  </div>
</template>

<script>
import { denormalize } from "@/helpers/JsonApiDenormalizer";
import { FileService } from "@/services/file.service";

export default {
  name: 'FilePickerButton',
  props: {
    type: {
      type: String,
    },
    typeId: {
      type: [Number, String],
    },
    accept: {
      type: String,
      default: "image/*",
    },
  },
  data: () => ({
    loading: false,
    presentSourceOptions: false, // the bottom sheet with Camera vs Library options
  }),
  methods: {
    snacktime(message) {
      this.$store.commit('snacktime', {
        text: message
      });
    },
    onUploadIntent() {
      if ( ! window.cordova) {
        this.onNormalBrowserUpload();
      }
      else {
        this.presentSourceOptions = true;
      }
    },
    getDefaultCordovaCameraOptions() {
      return {
          quality: 50, // where 50 is the default...
          // destinationType: Camera.DestinationType.DATA_URL,
          destinationType: Camera.DestinationType.FILE_URI,
          sourceType: Camera.PictureSourceType.CAMERA,  // CAMERA, PHOTOLIBRARY, SAVEDPHOTOALBUM
          allowEdit: false,
          encodingType: Camera.EncodingType.JPEG,
          targetWidth: 1920,
          // targetHeight: 1080,
          mediaType: Camera.MediaType.PICTURE,
          correctOrientation: true,
          saveToPhotoAlbum: false,
          cameraDirection: Camera.Direction.BACK,
        };
    },
    onSelectCamera() {
      let cameraOptions = Object.assign({}, this.getDefaultCordovaCameraOptions(), {
        sourceType: Camera.PictureSourceType.CAMERA
      });
      this.startCordovaCamera(cameraOptions);
    },
    onSelectLibrary() {
      let cameraOptions = Object.assign({}, this.getDefaultCordovaCameraOptions(), {
        sourceType: Camera.PictureSourceType.PHOTOLIBRARY
      });
      this.startCordovaCamera(cameraOptions);
    },
    startCordovaCamera(cameraOptions) {
      const vm = this;
      navigator.camera.getPicture(vm.onCordovaCameraSuccess, vm.onCordovaCameraFailure, cameraOptions);
    },
    onCordovaCameraSuccess(data) {
      // Reminder: This "success" callback is simply that the user successfully
      // selected an image to upload. Not that the upload has completed yet.
      this.loading = true;
      this.$emit("upload-start");
      this.uploadChosenCordovaImage(data).then(responseDataObject => {
        const upload = denormalize(responseDataObject);
        this.$emit("uploaded", upload);
      })
      .catch(err => {
        vm.snacktime(err.message);
      })
      .finally(() => {
        this.loading = false;
        this.$emit("upload-end");
      });
    },
    onCordovaCameraFailure(message) {
      message = message || 'Failed to select image for upload.';
      this.snacktime(message);
    },
    uploadChosenCordovaImage(data) {
      // For now assume we're only using FILE_URI and not DATA_URI (base64)
      const vm = this;
      const file_uri = data;
      const endpoint = `${process.env.VUE_APP_API_URL}/${process.env.VUE_APP_API_VER}/files`;
      return new Promise((resolve, reject) => {
        const win = r => {
          // Reminder: r is an object with responseCode, response, bytesSent etc.
          vm.snacktime("File uploaded successfully.");
          vm.presentSourceOptions = false;
          // This supports callbacks for cleanup success/failure
          navigator.camera.cleanup();
          // The Cordova xhr response comes back as a string so we need to parse ourselves
          const responseDataObject = JSON.parse(r.response);
          return resolve(responseDataObject);
        };
        const fail = error => {
          // Reminder: error is an object with code, source, target etc.
          const message = `Upload error occurred: ${error.code}`;
          vm.snacktime(message);
          // alert(`Error: ${JSON.stringify(error)}`);
          // This supports callbacks for cleanup success/failure
          navigator.camera.cleanup();
          return reject(new Error(message));
        };
        const options = new FileUploadOptions();
        options.fileKey = 'file';
        options.withCredentials = true;
        options.params = {
          fileable_id: vm.typeId,
          fileable_type: vm.type,
        };
        // Include Auth headers etc.
        let headers = {};
        const jwt = vm.$store.state.auth.token;
        if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
        options.headers = headers;

        const ft = new FileTransfer();
        ft.onprogress = function(progressEvent) {
          if (progressEvent.lengthComputable) {
            // Could emit progress
            let percent = Math.floor(progressEvent.loaded / progressEvent.total * 100);
          }
        };
        ft.upload(file_uri, encodeURI(endpoint), win, fail, options);
      });
    },
    onNormalBrowserUpload() {
      const e = new MouseEvent("click");
      this.$refs.inputFile.dispatchEvent(e);
    },
    async uploadFile(file) {
      try {
        this.$emit("upload-start");
        this.loading = true;

        const data = new FormData();
        data.append("file", file);

        if (this.type) data.append("fileable_type", this.type);
        if (this.typeId) data.append("fileable_id", this.typeId);

        const uploaded = await FileService.create(data).then((response) =>
          denormalize(response.data)
        );

        // Reset input value
        this.$refs.inputFile.value = "";

        this.$store.commit("snacktime", {
          text: "File has been uploaded.",
        });

        this.$emit("uploaded", uploaded);
      } catch (err) {
        throw err;
      } finally {
        this.loading = false;
        this.$emit("upload-end");
      }
    },
    onInputFileChange(e) {
      const file = e.target.files[0];

      if (file) {
        this.uploadFile(file);
      }
    },
  },
};
</script>
